import React, { useState, useEffect, useMemo } from 'react';
import { WebCryptoRamManager } from '../crypto/webcrypto-ram';
import { InterventionRecipe, DEFAULT_DOMAIN_RECIPES } from '../sync/recipe-types';
import { VoiceMemoModal } from './audio/VoiceMemoModal';
import { attachVoiceMemoToRecipe } from '../audio/recipe-assignment-service';

export type StrictnessTierType =
  | 'softPause'
  | 'reflectiveJournal'
  | 'groundingExercise'
  | 'coolingOffContract'
  | 'hardLock';

export interface PatientAuditLog {
  id: string;
  action: string;
  category: string;
  details: string;
  timestamp: string;
  aclMode: 'full_metadata' | 'local_slm_summary_only' | 'completely_isolated';
  payload: Record<string, any>;
}

export interface PatientRecord {
  id: string;
  name: string;
  avatar: string;
  pairedStatus: 'paired' | 'pending_sync' | 'review_requested';
  pairedDate: string;
  lastCheckIn: string;
  streakDays: number;
  pausesToday: number;
  activePlan: string;
  category: string;
  daytimeTier: StrictnessTierType;
  nighttimeTier: StrictnessTierType;
  nightStartHour: number; // e.g. 22 (10 PM)
  nightEndHour: number;   // e.g. 6 (6 AM)
  allowOverride: boolean;
  customCbtPrompt: string;
  voiceMemoAttached: boolean;
  voiceMemoTitle: string;
  voiceMemoUrl?: string;
  voiceMemoDuration?: number;
  voiceMemoTranscript?: string;
  voiceMemoStoragePath?: string;
  policySyncStatus: 'accepted' | 'pending_client_acceptance' | 'draft';
  auditLogs: PatientAuditLog[];
  assignedRecipe?: InterventionRecipe;
}

export interface PatientRosterViewProps {
  cryptoManager?: WebCryptoRamManager;
  coachId?: string;
  coachName?: string;
}

const TIER_LABELS: Record<StrictnessTierType, { name: string; desc: string; color: string; bg: string }> = {
  softPause: {
    name: 'Tier 1 (Soft Pause 10s)',
    desc: '10-second mindful breathing pause before app opens',
    color: '#16A34A',
    bg: '#E8F5E9',
  },
  reflectiveJournal: {
    name: 'Tier 2 (Reflective Journaling)',
    desc: 'CBT cognitive reframing check-in and urge surfing question',
    color: '#0284C7',
    bg: '#E0F2FE',
  },
  groundingExercise: {
    name: 'Tier 3 (Somatic Grounding)',
    desc: '3-minute 4-4-4 breathing mandala and sensory grounding',
    color: '#D97706',
    bg: '#FEF3C7',
  },
  coolingOffContract: {
    name: 'Tier 4 (Cooling-Off Contract)',
    desc: 'Time-locked delay (5-15 mins) with therapist voice memo',
    color: '#EA580C',
    bg: '#FFEDD5',
  },
  hardLock: {
    name: 'Tier 5 (Hard Lockout)',
    desc: 'Complete access block during designated high-risk hours',
    color: '#DC2626',
    bg: '#FEE2E2',
  },
};

export const PatientRosterView: React.FC<PatientRosterViewProps> = ({
  cryptoManager,
  coachId = 'coach_lead_01',
  coachName = 'Care Lead Practitioner',
}) => {
  const [patients, setPatients] = useState<PatientRecord[]>([]);

  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paired' | 'pending_sync' | 'review_requested'>('all');
  const [activeDrillTab, setActiveDrillTab] = useState<'plan' | 'strictness' | 'audit'>('plan');
  const [saveBanner, setSaveBanner] = useState<string | null>(null);
  const [selectedAuditLog, setSelectedAuditLog] = useState<PatientAuditLog | null>(null);
  const [isVoiceMemoModalOpen, setIsVoiceMemoModalOpen] = useState<boolean>(false);

  const selectedPatient = useMemo(
    () => patients.find((p) => p.id === selectedPatientId) || null,
    [patients, selectedPatientId]
  );

  const filteredPatients = useMemo(() => {
    return patients.filter((p) => {
      const matchesSearch =
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.activePlan.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || p.pairedStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [patients, searchTerm, statusFilter]);

  const handleUpdateSelectedPatient = (updates: Partial<PatientRecord>) => {
    if (!selectedPatientId) return;
    setPatients((prev) =>
      prev.map((p) => (p.id === selectedPatientId ? { ...p, ...updates } : p))
    );
  };

  const handleSavePatientPlan = () => {
    if (!selectedPatient) return;
    setSaveBanner('✓ Care Plan & CBT Prompts Saved to Client Profile');
    setTimeout(() => setSaveBanner(null), 3000);
  };

  const handleProposePolicyRecommendation = () => {
    if (!selectedPatient) return;
    const newLog: PatientAuditLog = {
      id: `log_prop_${Date.now()}`,
      action: 'policy_recommendation_proposed',
      category: selectedPatient.category,
      details: `Therapist recommended Daytime ${TIER_LABELS[selectedPatient.daytimeTier].name}, Nighttime ${TIER_LABELS[selectedPatient.nighttimeTier].name}`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric', year: 'numeric' }),
      aclMode: 'full_metadata',
      payload: {
        proposedBy: 'Therapist Dashboard',
        daytimeTier: selectedPatient.daytimeTier,
        nighttimeTier: selectedPatient.nighttimeTier,
        nightStartHour: selectedPatient.nightStartHour,
        nightEndHour: selectedPatient.nightEndHour,
        allowOverride: selectedPatient.allowOverride,
        status: 'pending_client_acceptance',
      },
    };

    setPatients((prev) =>
      prev.map((p) =>
        p.id === selectedPatient.id
          ? {
              ...p,
              policySyncStatus: 'pending_client_acceptance',
              pairedStatus: 'pending_sync',
              auditLogs: [newLog, ...p.auditLogs],
            }
          : p
      )
    );

    setSaveBanner('📤 Policy Recommendation Dispatched! Awaiting client in-app acceptance.');
    setTimeout(() => setSaveBanner(null), 3500);
  };

  const handleSimulateClientAcceptance = () => {
    if (!selectedPatient) return;
    const newLog: PatientAuditLog = {
      id: `log_acc_${Date.now()}`,
      action: 'policy_accepted_by_client',
      category: selectedPatient.category,
      details: 'Client confirmed and accepted collaborative policy in-app',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric', year: 'numeric' }),
      aclMode: 'full_metadata',
      payload: {
        acceptedAt: new Date().toISOString(),
        activeDaytimeTier: selectedPatient.daytimeTier,
        activeNighttimeTier: selectedPatient.nighttimeTier,
      },
    };

    setPatients((prev) =>
      prev.map((p) =>
        p.id === selectedPatient.id
          ? {
              ...p,
              policySyncStatus: 'accepted',
              pairedStatus: 'paired',
              auditLogs: [newLog, ...p.auditLogs],
            }
          : p
      )
    );

    setSaveBanner('✓ Client In-App Acceptance Verified! Policy is now active.');
    setTimeout(() => setSaveBanner(null), 3500);
  };

  const handleDisconnectPatient = (id: string) => {
    setPatients((prev) => prev.filter((p) => p.id !== id));
    if (selectedPatientId === id) {
      setSelectedPatientId(null);
    }
  };

  const getStatusBadge = (status: PatientRecord['pairedStatus'], name: string) => {
    switch (status) {
      case 'paired':
        return {
          chip: `${name} - Paired`,
          badge: '🟢 Paired',
          color: '#1B4332',
          bg: '#D8F3DC',
          border: '#95D5B2',
        };
      case 'pending_sync':
        return {
          chip: `${name} - Pending Sync`,
          badge: '🟡 Pending Sync',
          color: '#92400E',
          bg: '#FEF3C7',
          border: '#FDE68A',
        };
      case 'review_requested':
        return {
          chip: `${name} - Review Requested`,
          badge: '🟣 Review Requested',
          color: '#5B21B6',
          bg: '#EDE9FE',
          border: '#DDD6FE',
        };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Top Clinical Statistics Overview */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
      }}>
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '14px',
          padding: '18px 20px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748B' }}>Total Linked Patients</div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#1B4332', marginTop: '4px' }}>
            {patients.length} Active Clients
          </div>
        </div>

        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '14px',
          padding: '18px 20px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748B' }}>Average Habit Streak</div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#2D5A27', marginTop: '4px' }}>
            🔥 {Math.round(patients.reduce((acc, p) => acc + p.streakDays, 0) / (patients.length || 1))} Days
          </div>
        </div>

        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '14px',
          padding: '18px 20px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748B' }}>Mindful Pauses Today</div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#0369A1', marginTop: '4px' }}>
            🧘 {patients.reduce((acc, p) => acc + p.pausesToday, 0)} Total Intercepts
          </div>
        </div>

        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '14px',
          padding: '18px 20px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748B' }}>Security Architecture</div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#059669', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>🔒 Zero-Knowledge RAM Sync</span>
          </div>
        </div>
      </div>

      {/* Header & Filter Toolbar */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        padding: '20px 24px',
        border: '1px solid #E2E8F0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1B4332' }}>
            👥 Patient & Client Roster
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748B' }}>
            Manage linked clients, customize CBT cognitive restructuring, calibrate strictness tiers, and review decrypted settings audits.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Status Filter Chips */}
          <div style={{ display: 'inline-flex', backgroundColor: '#FAF8F5', padding: '4px', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
            {[
              { id: 'all', label: 'All Patients' },
              { id: 'paired', label: '🟢 Paired' },
              { id: 'pending_sync', label: '🟡 Pending Sync' },
              { id: 'review_requested', label: '🟣 Review' },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id as any)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '7px',
                  border: 'none',
                  backgroundColor: statusFilter === f.id ? '#2D5A27' : 'transparent',
                  color: statusFilter === f.id ? '#FFFFFF' : '#64748B',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          <input
            type="text"
            placeholder="🔍 Search patient or care plan..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '9px 14px',
              borderRadius: '10px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#FAF8F5',
              fontSize: '13px',
              width: '230px',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Notification Banner */}
      {saveBanner && (
        <div style={{
          backgroundColor: '#D8F3DC',
          color: '#1B4332',
          border: '1px solid #95D5B2',
          borderRadius: '12px',
          padding: '12px 20px',
          fontSize: '13px',
          fontWeight: 700,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(45,90,39,0.1)',
        }}>
          <span>{saveBanner}</span>
          <button
            onClick={() => setSaveBanner(null)}
            style={{ background: 'none', border: 'none', color: '#1B4332', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Main Content Split Grid: Patient Roster List + Interactive Drill-down */}
      <div style={{ display: 'grid', gridTemplateColumns: selectedPatient ? '1.1fr 1.3fr' : '1fr', gap: '24px', alignItems: 'start' }}>
        
        {/* Left Column: Patient List Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {filteredPatients.length === 0 ? (
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '14px',
              padding: '40px 20px',
              textAlign: 'center',
              color: '#64748B',
              border: '1px dashed #CBD5E1',
            }}>
              No patients match your search or filter criteria.
            </div>
          ) : (
            filteredPatients.map((patient) => {
              const isSelected = selectedPatient?.id === patient.id;
              const statusInfo = getStatusBadge(patient.pairedStatus, patient.name);
              const dayTier = TIER_LABELS[patient.daytimeTier];
              const nightTier = TIER_LABELS[patient.nighttimeTier];

              return (
                <div
                  key={patient.id}
                  onClick={() => setSelectedPatientId(patient.id)}
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    padding: '20px',
                    border: isSelected ? '2px solid #2D5A27' : '1px solid #E2E8F0',
                    boxShadow: isSelected ? '0 6px 20px rgba(45,90,39,0.15)' : '0 2px 6px rgba(0,0,0,0.02)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                  }}
                >
                  {/* Top Row: Avatar, Name, Status Badge, Streak */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '46px',
                        height: '46px',
                        borderRadius: '12px',
                        backgroundColor: '#E8F5E9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '22px',
                      }}>
                        {patient.avatar}
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: 800, fontSize: '16px', color: '#1E293B' }}>
                            {patient.name}
                          </span>
                          <span style={{
                            backgroundColor: statusInfo.bg,
                            color: statusInfo.color,
                            border: `1px solid ${statusInfo.border}`,
                            padding: '2px 8px',
                            borderRadius: '999px',
                            fontSize: '11px',
                            fontWeight: 700,
                          }}>
                            {statusInfo.badge}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#475569', fontWeight: 500, marginTop: '2px' }}>
                          {patient.activePlan}
                        </div>
                        {patient.assignedRecipe && (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            backgroundColor: '#F3E8FF',
                            color: '#6B21A8',
                            border: '1px solid #E9D5FF',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            marginTop: '4px',
                          }}>
                            <span>🧩 {patient.assignedRecipe.blocks.length}-Block Recipe:</span>
                            <span style={{ color: '#4C1D95' }}>
                              {patient.assignedRecipe.blocks.map(b => b.type).join(' ➔ ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                      <span style={{
                        backgroundColor: '#D8F3DC',
                        color: '#1B4332',
                        padding: '3px 9px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 700,
                      }}>
                        🔥 {patient.streakDays}-Day Streak
                      </span>
                      <span style={{ fontSize: '11px', color: '#64748B' }}>
                        {patient.pausesToday} pauses today
                      </span>
                    </div>
                  </div>

                  {/* Middle Row: Strictness Tiers & Hours Chips */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{
                      backgroundColor: dayTier.bg,
                      color: dayTier.color,
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}>
                      ☀️ Day: {dayTier.name}
                    </div>
                    <div style={{
                      backgroundColor: nightTier.bg,
                      color: nightTier.color,
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}>
                      🌙 Night ({patient.nightStartHour}:00-{patient.nightEndHour}:00): {nightTier.name}
                    </div>
                  </div>

                  {/* Bottom Row: Last check-in and Drill-down prompt */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: '1px solid #F1F5F9',
                    paddingTop: '10px',
                    fontSize: '11px',
                    color: '#94A3B8',
                  }}>
                    <span>⏱️ Last active {patient.lastCheckIn}</span>
                    <span style={{ color: '#2D5A27', fontWeight: 700 }}>
                      {isSelected ? 'Viewing Care Plan ➔' : 'Click to Manage ➔'}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right Column: Interactive Patient Drilldown Detail Panel */}
        {selectedPatient && (
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '18px',
            padding: '28px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}>
            {/* Drilldown Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #E2E8F0',
              paddingBottom: '16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '10px',
                  backgroundColor: '#E8F5E9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px',
                }}>
                  {selectedPatient.avatar}
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', color: '#1B4332', fontWeight: 800 }}>
                    {selectedPatient.name} — Care Management
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '3px' }}>
                    <span style={{ fontSize: '12px', color: '#64748B' }}>
                      ID: <code>{selectedPatient.id}</code>
                    </span>
                    <span style={{ fontSize: '11px', color: '#94A3B8' }}>•</span>
                    <span style={{
                      backgroundColor: selectedPatient.policySyncStatus === 'accepted' ? '#D8F3DC' : '#FEF3C7',
                      color: selectedPatient.policySyncStatus === 'accepted' ? '#1B4332' : '#92400E',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '11px',
                      fontWeight: 700,
                    }}>
                      {selectedPatient.policySyncStatus === 'accepted' ? '✓ Policy Accepted & Active' : '⏳ Pending Client In-App Acceptance'}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setSelectedPatientId(null)}
                style={{
                  backgroundColor: '#F1F5F9',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#475569',
                }}
              >
                ✕ Close
              </button>
            </div>

            {/* Drilldown Navigation Tabs */}
            <div style={{
              display: 'flex',
              backgroundColor: '#FAF8F5',
              padding: '4px',
              borderRadius: '10px',
              border: '1px solid #E2E8F0',
              gap: '4px',
            }}>
              {[
                { id: 'plan', label: '🧠 CBT Prompts & Media' },
                { id: 'strictness', label: '⏰ Strictness Schedules & Policy' },
                { id: 'audit', label: '🛡️ Decrypted Settings Audit' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveDrillTab(tab.id as any)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: activeDrillTab === tab.id ? '#2D5A27' : 'transparent',
                    color: activeDrillTab === tab.id ? '#FFFFFF' : '#64748B',
                    fontWeight: 700,
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Sub-tab 1: Care Plan & Custom CBT Restructuring */}
            {activeDrillTab === 'plan' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '6px' }}>
                    Active Clinical Focus Domain
                  </label>
                  <input
                    type="text"
                    value={selectedPatient.activePlan}
                    onChange={(e) => handleUpdateSelectedPatient({ activePlan: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #CBD5E1',
                      backgroundColor: '#FAF8F5',
                      fontSize: '13px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', margin: 0 }}>
                      Tailored CBT Cognitive Restructuring Prompt
                    </label>
                    <span style={{ fontSize: '11px', color: '#059669', fontWeight: 600 }}>Zero-Knowledge Synced</span>
                  </div>
                  <textarea
                    rows={4}
                    value={selectedPatient.customCbtPrompt}
                    onChange={(e) => handleUpdateSelectedPatient({ customCbtPrompt: e.target.value })}
                    placeholder="Enter tailored CBT reframing statement..."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #CBD5E1',
                      backgroundColor: '#FAF8F5',
                      fontSize: '13px',
                      fontFamily: 'inherit',
                      lineHeight: 1.5,
                      resize: 'vertical',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#64748B', marginTop: '4px' }}>
                    This prompt renders immediately when client encounters an intercepted trigger target.
                  </div>
                </div>

                {/* Supportive Voice Memo Attachment */}
                <div style={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #E2E8F0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '24px' }}>🎙️</span>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#1B4332' }}>
                          Therapist Supportive Voice Memo
                        </div>
                        <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>
                          {selectedPatient.voiceMemoAttached
                            ? `Attached: "${selectedPatient.voiceMemoTitle || 'Mindful Care Memo'}"`
                            : 'No voice memo currently attached'}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      {selectedPatient.voiceMemoAttached ? (
                        <>
                          <button
                            onClick={() => setIsVoiceMemoModalOpen(true)}
                            style={{
                              backgroundColor: '#E0F2FE',
                              color: '#0369A1',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '11px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            🎙️ Re-record / Change
                          </button>
                          <button
                            onClick={() => {
                              const updatedRecipe = selectedPatient.assignedRecipe ? {
                                ...selectedPatient.assignedRecipe,
                                blocks: selectedPatient.assignedRecipe.blocks.filter(
                                  (b) => !(b.type === 'support' && b.supportConfig?.variant === 'voice_video_note')
                                ),
                                updatedAt: new Date().toISOString(),
                              } : undefined;

                              handleUpdateSelectedPatient({
                                voiceMemoAttached: false,
                                voiceMemoTitle: '',
                                voiceMemoUrl: undefined,
                                voiceMemoDuration: undefined,
                                voiceMemoTranscript: undefined,
                                voiceMemoStoragePath: undefined,
                                assignedRecipe: updatedRecipe,
                              });
                              setSaveBanner('✓ Voice Memo Removed from Patient Profile and Recipe');
                              setTimeout(() => setSaveBanner(null), 3000);
                            }}
                            style={{
                              backgroundColor: '#FEE2E2',
                              color: '#DC2626',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '11px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            Remove Audio
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setIsVoiceMemoModalOpen(true)}
                          style={{
                            backgroundColor: '#E8F5E9',
                            color: '#2D5A27',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          + Record / Attach Voice Memo
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Audio Preview & Metadata if attached */}
                  {selectedPatient.voiceMemoAttached && (
                    <div style={{
                      backgroundColor: '#FFFFFF',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#334155' }}>
                          Preview Audio Note
                        </span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {selectedPatient.voiceMemoDuration !== undefined && (
                            <span style={{ fontSize: '10px', backgroundColor: '#F1F5F9', color: '#475569', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                              ⏱️ {selectedPatient.voiceMemoDuration.toFixed(1)}s
                            </span>
                          )}
                          {selectedPatient.voiceMemoStoragePath && (
                            <span style={{ fontSize: '10px', backgroundColor: '#EFF6FF', color: '#1D4ED8', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                              ☁️ Stored
                            </span>
                          )}
                        </div>
                      </div>

                      {selectedPatient.voiceMemoUrl && (
                        <audio controls src={selectedPatient.voiceMemoUrl} style={{ width: '100%', height: '36px' }} />
                      )}

                      {selectedPatient.voiceMemoTranscript && (
                        <div style={{ fontSize: '11px', color: '#475569', fontStyle: 'italic', backgroundColor: '#F8FAFC', padding: '6px 8px', borderRadius: '4px' }}>
                          "{selectedPatient.voiceMemoTranscript}"
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Assigned Composable Recipe (Milestone 2) */}
                {selectedPatient.assignedRecipe && (
                  <div style={{
                    backgroundColor: '#FAF5FF',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #E9D5FF',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px' }}>🧩</span>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: '#581C87' }}>
                            Assigned Composable Recipe: {selectedPatient.assignedRecipe.name}
                          </div>
                          <div style={{ fontSize: '11px', color: '#7E22CE', marginTop: '2px' }}>
                            Domain: <strong>{selectedPatient.assignedRecipe.domainId}</strong> • Updated: {selectedPatient.assignedRecipe.updatedAt.slice(0, 10)} • {selectedPatient.assignedRecipe.blocks.length} Modular Steps
                          </div>
                        </div>
                      </div>
                      <span style={{
                        backgroundColor: '#EDE9FE',
                        color: '#6D28D9',
                        padding: '3px 8px',
                        borderRadius: '999px',
                        fontSize: '10px',
                        fontWeight: 700,
                      }}>
                        Clinician Authored
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      backgroundColor: '#FFFFFF',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #F3E8FF',
                    }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#6B21A8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Execution Sequence (Intervention Flow)
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                        {selectedPatient.assignedRecipe.blocks.map((block, idx) => (
                          <div key={block.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '5px 10px',
                              borderRadius: '6px',
                              backgroundColor:
                                block.type === 'somatic' ? '#E8F5E9' :
                                block.type === 'cognitive' ? '#EFF6FF' :
                                block.type === 'friction' ? '#FFFBEB' : '#FDF2F8',
                              border: `1px solid ${
                                block.type === 'somatic' ? '#C8E6C9' :
                                block.type === 'cognitive' ? '#BFDBFE' :
                                block.type === 'friction' ? '#FDE68A' : '#FBCFE8'
                              }`,
                              fontSize: '12px',
                              fontWeight: 600,
                              color:
                                block.type === 'somatic' ? '#1B4332' :
                                block.type === 'cognitive' ? '#1E40AF' :
                                block.type === 'friction' ? '#92400E' : '#9D174D',
                            }}>
                              <span style={{ fontWeight: 800 }}>#{idx + 1}</span>
                              <span>{block.title}</span>
                              <span style={{ fontSize: '10px', opacity: 0.75 }}>({block.type})</span>
                            </div>
                            {idx < selectedPatient.assignedRecipe!.blocks.length - 1 && (
                              <span style={{ color: '#9CA3AF', fontWeight: 700 }}>➔</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sub-tab 2: Strictness Schedules & Collaborative Policy Sync */}
            {activeDrillTab === 'strictness' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <div style={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #E2E8F0',
                }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 700, color: '#1B4332' }}>
                    ☀️ Daytime Protection Strictness
                  </h4>
                  <select
                    value={selectedPatient.daytimeTier}
                    onChange={(e) => handleUpdateSelectedPatient({ daytimeTier: e.target.value as StrictnessTierType })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #CBD5E1',
                      backgroundColor: '#FFFFFF',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#1E293B',
                    }}
                  >
                    <option value="softPause">Tier 1: Soft Pause (10s Breathing Pause)</option>
                    <option value="reflectiveJournal">Tier 2: Reflective Journaling (CBT Reframing)</option>
                    <option value="groundingExercise">Tier 3: Somatic Grounding (3-Min Breathing Mandala)</option>
                    <option value="coolingOffContract">Tier 4: Cooling-Off Contract (Time-Locked Delay)</option>
                    <option value="hardLock">Tier 5: Hard Lockout (Strict Block)</option>
                  </select>
                  <div style={{ fontSize: '12px', color: '#64748B', marginTop: '6px' }}>
                    {TIER_LABELS[selectedPatient.daytimeTier].desc}
                  </div>
                </div>

                <div style={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #E2E8F0',
                }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 700, color: '#1B4332' }}>
                    🌙 Nighttime / High-Risk Hours Strictness
                  </h4>
                  <select
                    value={selectedPatient.nighttimeTier}
                    onChange={(e) => handleUpdateSelectedPatient({ nighttimeTier: e.target.value as StrictnessTierType })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #CBD5E1',
                      backgroundColor: '#FFFFFF',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#1E293B',
                    }}
                  >
                    <option value="softPause">Tier 1: Soft Pause (10s Breathing Pause)</option>
                    <option value="reflectiveJournal">Tier 2: Reflective Journaling (CBT Reframing)</option>
                    <option value="groundingExercise">Tier 3: Somatic Grounding (3-Min Breathing Mandala)</option>
                    <option value="coolingOffContract">Tier 4: Cooling-Off Contract (Time-Locked Delay)</option>
                    <option value="hardLock">Tier 5: Hard Lockout (Strict Block)</option>
                  </select>
                  <div style={{ fontSize: '12px', color: '#64748B', marginTop: '6px' }}>
                    {TIER_LABELS[selectedPatient.nighttimeTier].desc}
                  </div>

                  {/* Night Window Hours */}
                  <div style={{ display: 'flex', gap: '14px', marginTop: '12px', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748B', display: 'block', marginBottom: '4px' }}>
                        Night Window Starts
                      </label>
                      <select
                        value={selectedPatient.nightStartHour}
                        onChange={(e) => handleUpdateSelectedPatient({ nightStartHour: Number(e.target.value) })}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                      >
                        <option value={20}>8:00 PM (20:00)</option>
                        <option value={21}>9:00 PM (21:00)</option>
                        <option value={22}>10:00 PM (22:00)</option>
                        <option value={23}>11:00 PM (23:00)</option>
                      </select>
                    </div>

                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748B', display: 'block', marginBottom: '4px' }}>
                        Night Window Ends
                      </label>
                      <select
                        value={selectedPatient.nightEndHour}
                        onChange={(e) => handleUpdateSelectedPatient({ nightEndHour: Number(e.target.value) })}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                      >
                        <option value={5}>5:00 AM (05:00)</option>
                        <option value={6}>6:00 AM (06:00)</option>
                        <option value={7}>7:00 AM (07:00)</option>
                        <option value={8}>8:00 AM (08:00)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Collaborative Policy Recommendation & Acceptance Section */}
                <div style={{
                  backgroundColor: '#FAF8F5',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #E6DFD5',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#1B4332' }}>
                      🤝 Collaborative Policy Sync
                    </span>
                    <span style={{
                      backgroundColor: selectedPatient.policySyncStatus === 'accepted' ? '#D8F3DC' : '#FEF3C7',
                      color: selectedPatient.policySyncStatus === 'accepted' ? '#1B4332' : '#92400E',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '11px',
                      fontWeight: 700,
                    }}>
                      {selectedPatient.policySyncStatus === 'accepted' ? '✓ Accepted by Client' : '⏳ Awaiting In-App Acceptance'}
                    </span>
                  </div>

                  <p style={{ fontSize: '12px', color: '#475569', margin: '0 0 12px 0', lineHeight: 1.5 }}>
                    Policy recommendations are sent to the client’s device as a collaborative suggestion. The client receives an in-app prompt to review and accept the strictness changes.
                  </p>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={handleProposePolicyRecommendation}
                      style={{
                        backgroundColor: '#2D5A27',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '9px 16px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      📤 Propose Policy Recommendation to Client
                    </button>

                    {selectedPatient.policySyncStatus === 'pending_client_acceptance' && (
                      <button
                        onClick={handleSimulateClientAcceptance}
                        style={{
                          backgroundColor: '#E0F2FE',
                          color: '#0369A1',
                          border: '1px solid #BAE6FD',
                          borderRadius: '8px',
                          padding: '9px 14px',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        📲 Simulate Client In-App Acceptance
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Sub-tab 3: Decrypted Settings Audit Logs */}
            {activeDrillTab === 'audit' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#1B4332' }}>
                    Zero-Knowledge Decrypted Telemetry for {selectedPatient.name}
                  </div>
                  <span style={{ fontSize: '11px', color: '#059669', fontWeight: 600 }}>
                    🔒 In-Memory Decryption
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: selectedAuditLog ? '1.2fr 1fr' : '1fr', gap: '12px' }}>
                  {/* Audit Records List */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '280px', overflowY: 'auto' }}>
                    {selectedPatient.auditLogs.map((log) => {
                      const isLogSelected = selectedAuditLog?.id === log.id;
                      const mediaTel =
                        log.payload?.mediaPlaybackTelemetry ||
                        (log.payload?.durationSeconds !== undefined
                          ? {
                              mediaType: 'audio' as const,
                              playedDurationSeconds: log.payload.durationSeconds,
                              totalDurationSeconds: log.payload.totalDurationSeconds || log.payload.durationSeconds,
                              completedFullListen: log.payload.durationSeconds >= (log.payload.totalDurationSeconds || log.payload.durationSeconds),
                              volumeLevel: log.payload.volumeLevel ?? 0.85,
                              isMuted: !!log.payload.isMuted,
                              promptId: log.payload.promptId || 'prompt_memo',
                              sessionId: log.payload.sessionId || 'sess_memo',
                            }
                          : null);

                      const percentListened = mediaTel
                        ? Math.min(
                            100,
                            Math.round(
                              ((mediaTel.playedDurationSeconds || 0) /
                                (mediaTel.totalDurationSeconds || 1)) *
                                100
                            )
                          )
                        : 0;

                      return (
                        <div
                          key={log.id}
                          onClick={() => setSelectedAuditLog(log)}
                          style={{
                            padding: '10px 14px',
                            borderRadius: '8px',
                            backgroundColor: isLogSelected ? '#EFF6FF' : '#F8FAFC',
                            border: `1px solid ${isLogSelected ? '#3B82F6' : '#E2E8F0'}`,
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 700, fontSize: '12px', color: '#1E293B' }}>
                              {log.action}
                            </span>
                            <span style={{
                              backgroundColor: log.aclMode === 'full_metadata' ? '#E0F2FE' : '#FEF3C7',
                              color: log.aclMode === 'full_metadata' ? '#0369A1' : '#92400E',
                              padding: '2px 6px',
                              borderRadius: '999px',
                              fontSize: '10px',
                              fontWeight: 700,
                            }}>
                              {log.aclMode}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#475569', marginTop: '3px' }}>
                            {log.details}
                          </div>

                          {/* Inline Media Telemetry Summary if present */}
                          {mediaTel && (
                            <div
                              style={{
                                marginTop: '6px',
                                padding: '6px 8px',
                                backgroundColor: '#FFFFFF',
                                borderRadius: '6px',
                                border: '1px solid #E2E8F0',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#334155' }}>
                                  🎙️ Media Telemetry: {mediaTel.playedDurationSeconds}s / {mediaTel.totalDurationSeconds}s
                                </span>
                                {mediaTel.completedFullListen ? (
                                  <span
                                    style={{
                                      backgroundColor: '#DCFCE7',
                                      color: '#15803D',
                                      padding: '1px 5px',
                                      borderRadius: '4px',
                                      fontSize: '9px',
                                      fontWeight: 700,
                                    }}
                                  >
                                    ✓ 100% Full Listen
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      backgroundColor: '#FEF3C7',
                                      color: '#B45309',
                                      padding: '1px 5px',
                                      borderRadius: '4px',
                                      fontSize: '9px',
                                      fontWeight: 700,
                                    }}
                                  >
                                    🎧 Partial ({percentListened}%)
                                  </span>
                                )}
                              </div>

                              <div style={{ width: '100%', height: '4px', backgroundColor: '#E2E8F0', borderRadius: '2px', overflow: 'hidden' }}>
                                <div
                                  style={{
                                    width: `${percentListened}%`,
                                    height: '100%',
                                    backgroundColor: mediaTel.completedFullListen ? '#10B981' : '#F59E0B',
                                    borderRadius: '2px',
                                  }}
                                />
                              </div>

                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#64748B' }}>
                                <span>{mediaTel.isMuted ? '🔇 Audio Muted' : `🔊 Volume: ${Math.round((mediaTel.volumeLevel ?? 1) * 100)}%`}</span>
                                <span>{percentListened}% listened</span>
                              </div>
                            </div>
                          )}

                          <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '3px' }}>
                            {log.timestamp}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Audit Record Payload Inspector */}
                  {selectedAuditLog && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {(() => {
                        const mediaTel =
                          selectedAuditLog.payload?.mediaPlaybackTelemetry ||
                          (selectedAuditLog.payload?.durationSeconds !== undefined
                            ? {
                                mediaType: 'audio' as const,
                                playedDurationSeconds: selectedAuditLog.payload.durationSeconds,
                                totalDurationSeconds: selectedAuditLog.payload.totalDurationSeconds || selectedAuditLog.payload.durationSeconds,
                                completedFullListen: selectedAuditLog.payload.durationSeconds >= (selectedAuditLog.payload.totalDurationSeconds || selectedAuditLog.payload.durationSeconds),
                                volumeLevel: selectedAuditLog.payload.volumeLevel ?? 0.85,
                                isMuted: !!selectedAuditLog.payload.isMuted,
                                promptId: selectedAuditLog.payload.promptId || 'prompt_memo',
                                sessionId: selectedAuditLog.payload.sessionId || 'sess_memo',
                              }
                            : null);

                        if (!mediaTel) return null;
                        const percentListened = Math.min(
                          100,
                          Math.round(
                            ((mediaTel.playedDurationSeconds || 0) /
                              (mediaTel.totalDurationSeconds || 1)) *
                              100
                          )
                        );

                        return (
                          <div
                            style={{
                              backgroundColor: '#F8FAFC',
                              borderRadius: '10px',
                              padding: '12px',
                              border: '1px solid #CBD5E1',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '11px', fontWeight: 800, color: '#1E293B' }}>
                                🎙️ Therapist Audio Engagement
                              </span>
                              {mediaTel.completedFullListen ? (
                                <span
                                  style={{
                                    backgroundColor: '#DCFCE7',
                                    color: '#15803D',
                                    padding: '2px 8px',
                                    borderRadius: '999px',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                  }}
                                >
                                  ✓ Completed Full Listen
                                </span>
                              ) : (
                                <span
                                  style={{
                                    backgroundColor: '#FEF3C7',
                                    color: '#B45309',
                                    padding: '2px 8px',
                                    borderRadius: '999px',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                  }}
                                >
                                  🎧 Partial Listen ({percentListened}%)
                                </span>
                              )}
                            </div>

                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#475569', marginBottom: '2px' }}>
                                <span>Duration: {mediaTel.playedDurationSeconds}s / {mediaTel.totalDurationSeconds}s</span>
                                <span>{percentListened}%</span>
                              </div>
                              <div style={{ width: '100%', height: '6px', backgroundColor: '#E2E8F0', borderRadius: '3px', overflow: 'hidden' }}>
                                <div
                                  style={{
                                    width: `${percentListened}%`,
                                    height: '100%',
                                    backgroundColor: mediaTel.completedFullListen ? '#10B981' : '#3B82F6',
                                    borderRadius: '3px',
                                  }}
                                />
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: '8px', fontSize: '10px', color: '#475569' }}>
                              <span><strong>Volume:</strong> {Math.round((mediaTel.volumeLevel ?? 1) * 100)}%</span>
                              <span>•</span>
                              <span>
                                <strong>Status:</strong>{' '}
                                {mediaTel.isMuted ? (
                                  <span style={{ color: '#DC2626', fontWeight: 700 }}>🔇 Audio Muted</span>
                                ) : (
                                  <span style={{ color: '#16A34A', fontWeight: 700 }}>🔊 Unmuted</span>
                                )}
                              </span>
                            </div>
                          </div>
                        );
                      })()}

                      <div style={{
                        backgroundColor: '#0F172A',
                        borderRadius: '10px',
                        padding: '12px',
                        color: '#E2E8F0',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        maxHeight: '280px',
                        overflowY: 'auto',
                      }}>
                        <div style={{ color: '#10B981', fontWeight: 'bold', marginBottom: '6px' }}>
                          // Decrypted Payload
                        </div>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(selectedAuditLog.payload, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bottom Actions Bar */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: '1px solid #E2E8F0',
              paddingTop: '18px',
              gap: '12px',
            }}>
              <button
                onClick={handleSavePatientPlan}
                style={{
                  flex: 1,
                  backgroundColor: '#2D5A27',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(45,90,39,0.25)',
                }}
              >
                💾 Save & Deploy Care Plan
              </button>

              <button
                onClick={() => handleDisconnectPatient(selectedPatient.id)}
                style={{
                  backgroundColor: '#FEE2E2',
                  color: '#DC2626',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px 18px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Revoke Link
              </button>
            </div>
          </div>
        )}

        {selectedPatient && (
          <VoiceMemoModal
            isOpen={isVoiceMemoModalOpen}
            onClose={() => setIsVoiceMemoModalOpen(false)}
            onSave={(memo) => {
              const updatedRecipe = attachVoiceMemoToRecipe({
                patientId: selectedPatient.id,
                patientName: selectedPatient.name,
                coachId,
                coachName,
                domainId: selectedPatient.category || 'mindful_media',
                memoTitle: memo.mediaTitle,
                transcript: memo.transcript,
                mediaUrl: memo.mediaUrl,
                storagePath: memo.storagePath,
                durationSeconds: memo.durationSeconds,
                existingRecipe: selectedPatient.assignedRecipe,
              });

              handleUpdateSelectedPatient({
                voiceMemoAttached: true,
                voiceMemoTitle: memo.mediaTitle,
                voiceMemoUrl: memo.mediaUrl,
                voiceMemoDuration: memo.durationSeconds,
                voiceMemoTranscript: memo.transcript,
                voiceMemoStoragePath: memo.storagePath,
                assignedRecipe: updatedRecipe,
              });

              setSaveBanner('✓ Voice Memo Attached and Assigned to Patient Recipe');
              setTimeout(() => setSaveBanner(null), 3000);
            }}
            coachId={coachId}
            coachName={coachName}
            patientId={selectedPatient.id}
            patientName={selectedPatient.name}
            domainId={selectedPatient.category || 'mindful_media'}
            domainName={selectedPatient.category ? selectedPatient.category.replace('_', ' ') : 'Mindful Habit'}
            initialTitle={selectedPatient.voiceMemoTitle || 'Therapist Supportive Voice Memo'}
            initialTranscript={selectedPatient.voiceMemoTranscript || ''}
            initialMediaUrl={selectedPatient.voiceMemoUrl}
            initialStoragePath={selectedPatient.voiceMemoStoragePath}
            initialDuration={selectedPatient.voiceMemoDuration}
          />
        )}
      </div>
    </div>
  );
};

export default PatientRosterView;
